import { useState } from "react";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

const FIELDS = [
  ["name", "Project name", "text"], ["spv", "SPV / company", "text"],
  ["status", "Status", "select:Live,Pipeline,Exited"], ["owner_code", "Owner code", "owner"],
  ["proforma_signed", "Proforma signed", "date"], ["report_date", "Report date", "date"],
  ["proforma.equity", "Equity requirement – proforma (£)", "number"], ["proforma.profit", "Proforma EPIL profit (£)", "number"],
  ["proforma.equity_return_date", "Proforma equity return date", "date"], ["proforma.profit_date", "Proforma profit date", "date"],
  ["projected.profit", "Latest projected profit (£)", "number"], ["projected.profit_date", "Projected profit date", "date"],
  ["projected.equity_return_date", "Projected equity return date", "date"], ["pipeline", "Pipeline – equity not yet agreed (£)", "number"],
  ["am_equity_pct", "AM equity % override (blank = owner-code default)", "pct"], ["am_profit_pct", "AM profit % override (blank = default)", "pct"],
];

const get = (p, k) => { const [a, b] = k.split("."); return b ? p[a]?.[b] : p[k]; };
const toForm = (p, k, t) => { const v = get(p, k); if (v === null || v === undefined) return ""; return t === "pct" ? String(+(v * 100).toFixed(4)) : String(v); };

export function EditProjectDialog({ project, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const locked = (k) => !!project.proforma_locked && k.startsWith("proforma");
  const init = () => {
    const f = { notes: project.notes || "", include: project.include };
    FIELDS.forEach(([k, , t]) => { f[k] = toForm(project, k, t); });
    setForm(f);
  };
  const save = async () => {
    const fields = {};
    FIELDS.forEach(([k, , t]) => {
      if (form[k] === toForm(project, k, t)) return;
      fields[k] = form[k] === "" ? null : t === "pct" ? Number(form[k]) / 100 : t === "number" ? Number(form[k]) : form[k];
    });
    if (form.notes !== (project.notes || "")) fields.notes = form.notes;
    if (form.include !== project.include) fields.include = form.include;
    if (!Object.keys(fields).length) return setOpen(false);
    if (Object.keys(fields).some((k) => k.startsWith("projected") || k === "pipeline") && !(form.reason || "").trim()) return toast.error("Please give a reason for the forecast change");
    setBusy(true);
    try { const { data } = await api.patch(`/projects/${project.id}`, { fields, reason: form.reason || null }); onSaved(data); toast.success("Project saved"); setOpen(false); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const input = (k, t) => {
    if (t.startsWith("select:") || t === "owner") {
      const opts = t === "owner" ? project.owner_codes : t.slice(7).split(",");
      return (
        <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-${k.replace(".", "-")}`}>
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    return <Input type={t === "date" ? "date" : t === "text" ? "text" : "number"} step="any" disabled={locked(k)} title={locked(k) ? "Proforma figures are locked — changes go in the current forecast" : undefined} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-${k.replace(".", "-")}`} />;
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) init(); setOpen(o); }}>
      <DialogTrigger asChild><Button variant="outline" data-testid="edit-project-btn"><Pencil className="mr-2 h-4 w-4" /> Edit details</Button></DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Edit project details</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map(([k, label, t]) => (
            <div key={k} className={`space-y-1.5 ${k === "name" ? "sm:col-span-2" : ""}`}><Label className="text-xs">{label}</Label>{input(k, t)}</div>
          ))}
          <div className="flex items-center gap-3 sm:col-span-2">
            <Switch checked={!!form.include} onCheckedChange={(v) => setForm({ ...form, include: v })} data-testid="edit-include" />
            <Label>Include in all reports</Label>
          </div>
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Notes</Label>
            <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-notes" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Reason for forecast change (required when projected figures or pipeline change)</Label>
            <Input value={form.reason || ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="edit-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="edit-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="edit-save-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
